/**
 * GWP (Gift With Purchase) Management Component
 * Manages promotional gift campaigns, inventory tracking, and distribution per store.
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  CardGiftcard as GiftIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Calculate as CalculateIcon,
  LocalShipping as DistributeIcon,
  Inventory as StockIcon,
  Warning as WarningIcon,
  Campaign as CampaignIcon,
  Redeem as RedeemIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GWPDashboard {
  active_campaigns: number;
  total_gifts_in_stock: number;
  total_deficit: number;
  total_distributed: number;
}

interface GWPCampaign {
  campaign_id: string;
  campaign_name: string;
  trigger_product_id: string;
  trigger_category?: string;
  trigger_brand?: string;
  trigger_min_quantity: number;
  gift_description: string;
  gift_quantity: number;
  gift_unit_cost_mad: number;
  start_date: string;
  end_date: string;
  status: 'planned' | 'active' | 'ended' | 'archived';
  auto_calculate_needs: boolean;
}

interface GWPStoreDetail {
  store_id: string;
  store_name: string;
  current_gift_stock: number;
  allocated: number;
  distributed: number;
  estimated_need: number;
  deficit: number;
}

interface GWPCampaignDetail {
  campaign: GWPCampaign;
  stores: GWPStoreDetail[];
}

interface CampaignFormData {
  campaign_name: string;
  trigger_product_id: string;
  trigger_category: string;
  trigger_brand: string;
  trigger_min_quantity: number;
  gift_description: string;
  gift_quantity: number;
  gift_unit_cost_mad: number;
  start_date: string;
  end_date: string;
  auto_calculate_needs: boolean;
}

const INITIAL_FORM: CampaignFormData = {
  campaign_name: '',
  trigger_product_id: '',
  trigger_category: '',
  trigger_brand: '',
  trigger_min_quantity: 1,
  gift_description: '',
  gift_quantity: 0,
  gift_unit_cost_mad: 0,
  start_date: '',
  end_date: '',
  auto_calculate_needs: true,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const statusColor = (status: string): 'info' | 'success' | 'default' => {
  switch (status) {
    case 'planned':
      return 'info';
    case 'active':
      return 'success';
    default:
      return 'default';
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const GWPManagement: React.FC = () => {
  const { t } = useTranslation();
  const { fmtCurrencyLabel } = useCurrencyFormat();
  const queryClient = useQueryClient();

  // --- UI State ---
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState<CampaignFormData>(INITIAL_FORM);
  const [expandedCampaignId, setExpandedCampaignId] = useState<string | null>(null);
  const [distributeDialogOpen, setDistributeDialogOpen] = useState(false);
  const [updateStockDialogOpen, setUpdateStockDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState<{ campaignId: string; storeId: string; storeName: string } | null>(null);
  const [distributeQuantity, setDistributeQuantity] = useState<number>(0);
  const [newStockQuantity, setNewStockQuantity] = useState<number>(0);

  // --- Data Queries ---

  const {
    data: dashboard,
    isLoading: dashboardLoading,
    error: dashboardError,
  } = useQuery<GWPDashboard>({
    queryKey: ['gwpDashboard'],
    queryFn: async () => {
      const response = await api.getGWPDashboard();
      const d = response.data ?? {};
      return { ...d, total_gifts_in_stock: d.total_gifts_in_stock ?? d.total_gift_stock ?? 0 };
    },
  });

  const campaignParams = statusFilter !== 'all' ? { status: statusFilter } : {};

  const {
    data: campaigns,
    isLoading: campaignsLoading,
    error: campaignsError,
  } = useQuery<GWPCampaign[]>({
    queryKey: ['gwpCampaigns', statusFilter],
    queryFn: async () => {
      const response = await api.getGWPCampaigns(campaignParams);
      return response.data;
    },
  });

  const {
    data: campaignDetail,
    isLoading: detailLoading,
  } = useQuery<GWPCampaignDetail>({
    queryKey: ['gwpCampaignDetail', expandedCampaignId],
    queryFn: async () => {
      const response = await api.getGWPCampaignDetail(expandedCampaignId!);
      return response.data;
    },
    enabled: !!expandedCampaignId,
  });

  // --- Mutations ---

  const createCampaignMutation = useMutation({
    mutationFn: (data: CampaignFormData) => api.createGWPCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gwpCampaigns'] });
      queryClient.invalidateQueries({ queryKey: ['gwpDashboard'] });
      setCreateDialogOpen(false);
      setFormData(INITIAL_FORM);
    },
  });

  const calculateNeedsMutation = useMutation({
    mutationFn: (campaignId: string) => api.calculateGWPNeeds(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gwpCampaignDetail', expandedCampaignId] });
      queryClient.invalidateQueries({ queryKey: ['gwpDashboard'] });
    },
  });

  const distributeMutation = useMutation({
    mutationFn: ({ campaignId, storeId, quantity }: { campaignId: string; storeId: string; quantity: number }) =>
      api.distributeGift(campaignId, storeId, quantity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gwpCampaignDetail', expandedCampaignId] });
      queryClient.invalidateQueries({ queryKey: ['gwpDashboard'] });
      setDistributeDialogOpen(false);
      setDistributeQuantity(0);
      setSelectedStore(null);
    },
  });

  const updateStockMutation = useMutation({
    mutationFn: ({ campaignId, storeId, stock }: { campaignId: string; storeId: string; stock: number }) =>
      api.updateGWPStock(campaignId, storeId, { stock }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gwpCampaignDetail', expandedCampaignId] });
      queryClient.invalidateQueries({ queryKey: ['gwpDashboard'] });
      setUpdateStockDialogOpen(false);
      setNewStockQuantity(0);
      setSelectedStore(null);
    },
  });

  // --- Handlers ---

  const handleFormChange = (field: keyof CampaignFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateSubmit = () => {
    createCampaignMutation.mutate(formData);
  };

  const handleRowClick = (campaignId: string) => {
    setExpandedCampaignId((prev) => (prev === campaignId ? null : campaignId));
  };

  const handleOpenDistribute = (campaignId: string, storeId: string, storeName: string) => {
    setSelectedStore({ campaignId, storeId, storeName });
    setDistributeQuantity(0);
    setDistributeDialogOpen(true);
  };

  const handleOpenUpdateStock = (campaignId: string, storeId: string, storeName: string) => {
    setSelectedStore({ campaignId, storeId, storeName });
    setNewStockQuantity(0);
    setUpdateStockDialogOpen(true);
  };

  const handleDistributeSubmit = () => {
    if (selectedStore) {
      distributeMutation.mutate({
        campaignId: selectedStore.campaignId,
        storeId: selectedStore.storeId,
        quantity: distributeQuantity,
      });
    }
  };

  const handleUpdateStockSubmit = () => {
    if (selectedStore) {
      updateStockMutation.mutate({
        campaignId: selectedStore.campaignId,
        storeId: selectedStore.storeId,
        stock: newStockQuantity,
      });
    }
  };

  const anyError = dashboardError || campaignsError;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GiftIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4">{t('gwp.title')}</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          {t('gwp.addCampaign')}
        </Button>
      </Box>

      {anyError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('gwp.errorLoading')}
        </Alert>
      )}

      {/* ---- Dashboard KPI Cards ---- */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CampaignIcon color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  {t('gwp.activeCampaigns')}
                </Typography>
              </Box>
              {dashboardLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Typography variant="h4">{dashboard?.active_campaigns ?? 0}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <StockIcon color="info" />
                <Typography variant="subtitle2" color="text.secondary">
                  {t('gwp.totalGiftsInStock')}
                </Typography>
              </Box>
              {dashboardLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Typography variant="h4">{dashboard?.total_gifts_in_stock ?? 0}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningIcon color={(dashboard?.total_deficit ?? 0) > 0 ? 'error' : 'action'} />
                <Typography variant="subtitle2" color="text.secondary">
                  {t('gwp.totalDeficit')}
                </Typography>
              </Box>
              {dashboardLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Typography
                  variant="h4"
                  sx={{ color: (dashboard?.total_deficit ?? 0) > 0 ? 'error.main' : 'text.primary' }}
                >
                  {dashboard?.total_deficit ?? 0}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <RedeemIcon color="success" />
                <Typography variant="subtitle2" color="text.secondary">
                  {t('gwp.totalDistributed')}
                </Typography>
              </Box>
              {dashboardLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Typography variant="h4">{dashboard?.total_distributed ?? 0}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ---- Campaigns Table ---- */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant="h6">{t('gwp.campaignsList')}</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t('gwp.filterStatus')}</InputLabel>
              <Select
                value={statusFilter}
                label={t('gwp.filterStatus')}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">{t('gwp.statusAll')}</MenuItem>
                <MenuItem value="planned">{t('gwp.statusPlanned')}</MenuItem>
                <MenuItem value="active">{t('gwp.statusActive')}</MenuItem>
                <MenuItem value="ended">{t('gwp.statusEnded')}</MenuItem>
                <MenuItem value="archived">{t('gwp.statusArchived')}</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title={t('gwp.refresh')}>
              <IconButton
                onClick={() => {
                  queryClient.invalidateQueries({ queryKey: ['gwpCampaigns'] });
                  queryClient.invalidateQueries({ queryKey: ['gwpDashboard'] });
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {campaignsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>{t('gwp.campaignName')}</TableCell>
                  <TableCell>{t('gwp.triggerProduct')}</TableCell>
                  <TableCell>{t('gwp.gift')}</TableCell>
                  <TableCell align="right">{t('gwp.giftQty')}</TableCell>
                  <TableCell>{t('gwp.startDate')}</TableCell>
                  <TableCell>{t('gwp.endDate')}</TableCell>
                  <TableCell>{t('gwp.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaigns && campaigns.length > 0 ? (
                  campaigns.map((campaign) => (
                    <React.Fragment key={campaign.campaign_id}>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedCampaignId === campaign.campaign_id ? 'unset' : undefined } }}
                        onClick={() => handleRowClick(campaign.campaign_id)}
                      >
                        <TableCell sx={{ width: 48 }}>
                          <IconButton size="small">
                            {expandedCampaignId === campaign.campaign_id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {campaign.campaign_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{campaign.trigger_product_id}</TableCell>
                        <TableCell>{campaign.gift_description}</TableCell>
                        <TableCell align="right">{campaign.gift_quantity}</TableCell>
                        <TableCell>{campaign.start_date}</TableCell>
                        <TableCell>{campaign.end_date}</TableCell>
                        <TableCell>
                          <Chip
                            label={t(`gwp.status${campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}`)}
                            color={statusColor(campaign.status)}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>

                      {/* Expanded Detail Row */}
                      <TableRow>
                        <TableCell colSpan={8} sx={{ p: 0 }}>
                          <Collapse in={expandedCampaignId === campaign.campaign_id} timeout="auto" unmountOnExit>
                            <CampaignDetailSection
                              campaignId={campaign.campaign_id}
                              autoCalculateNeeds={campaign.auto_calculate_needs}
                              detail={expandedCampaignId === campaign.campaign_id ? campaignDetail ?? null : null}
                              loading={expandedCampaignId === campaign.campaign_id && detailLoading}
                              onCalculateNeeds={() => calculateNeedsMutation.mutate(campaign.campaign_id)}
                              calculateNeedsLoading={calculateNeedsMutation.isPending}
                              onDistribute={handleOpenDistribute}
                              onUpdateStock={handleOpenUpdateStock}
                              t={t}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        {t('gwp.noCampaigns')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ---- Create Campaign Dialog ---- */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('gwp.createCampaign')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('gwp.fieldCampaignName')}
              value={formData.campaign_name}
              onChange={(e) => handleFormChange('campaign_name', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label={t('gwp.fieldTriggerProductId')}
              value={formData.trigger_product_id}
              onChange={(e) => handleFormChange('trigger_product_id', e.target.value)}
              fullWidth
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label={t('gwp.fieldTriggerCategory')}
                  value={formData.trigger_category}
                  onChange={(e) => handleFormChange('trigger_category', e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('gwp.fieldTriggerBrand')}
                  value={formData.trigger_brand}
                  onChange={(e) => handleFormChange('trigger_brand', e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
            <TextField
              label={t('gwp.fieldTriggerMinQuantity')}
              type="number"
              value={formData.trigger_min_quantity}
              onChange={(e) => handleFormChange('trigger_min_quantity', parseInt(e.target.value) || 0)}
              fullWidth
              inputProps={{ min: 1 }}
            />
            <TextField
              label={t('gwp.fieldGiftDescription')}
              value={formData.gift_description}
              onChange={(e) => handleFormChange('gift_description', e.target.value)}
              fullWidth
              required
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label={t('gwp.fieldGiftQuantity')}
                  type="number"
                  value={formData.gift_quantity}
                  onChange={(e) => handleFormChange('gift_quantity', parseInt(e.target.value) || 0)}
                  fullWidth
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('gwp.fieldGiftUnitCostMad')}
                  type="number"
                  value={formData.gift_unit_cost_mad}
                  onChange={(e) => handleFormChange('gift_unit_cost_mad', parseFloat(e.target.value) || 0)}
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{ endAdornment: <Typography variant="caption">{fmtCurrencyLabel()}</Typography> }}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label={t('gwp.fieldStartDate')}
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleFormChange('start_date', e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('gwp.fieldEndDate')}
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleFormChange('end_date', e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </Grid>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.auto_calculate_needs}
                  onChange={(e) => handleFormChange('auto_calculate_needs', e.target.checked)}
                />
              }
              label={t('gwp.fieldAutoCalculateNeeds')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('gwp.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={
              createCampaignMutation.isPending ||
              !formData.campaign_name ||
              !formData.gift_description ||
              !formData.start_date ||
              !formData.end_date
            }
          >
            {createCampaignMutation.isPending ? <CircularProgress size={20} /> : t('gwp.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Distribute Gift Dialog ---- */}
      <Dialog open={distributeDialogOpen} onClose={() => setDistributeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('gwp.distributeGift')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('gwp.distributeToStore', { store: selectedStore?.storeName ?? '' })}
          </Typography>
          <TextField
            label={t('gwp.fieldQuantity')}
            type="number"
            value={distributeQuantity}
            onChange={(e) => setDistributeQuantity(parseInt(e.target.value) || 0)}
            fullWidth
            inputProps={{ min: 1 }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDistributeDialogOpen(false)}>{t('gwp.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleDistributeSubmit}
            disabled={distributeMutation.isPending || distributeQuantity <= 0}
          >
            {distributeMutation.isPending ? <CircularProgress size={20} /> : t('gwp.distribute')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Update Stock Dialog ---- */}
      <Dialog open={updateStockDialogOpen} onClose={() => setUpdateStockDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('gwp.updateStock')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('gwp.updateStockForStore', { store: selectedStore?.storeName ?? '' })}
          </Typography>
          <TextField
            label={t('gwp.fieldNewStockQuantity')}
            type="number"
            value={newStockQuantity}
            onChange={(e) => setNewStockQuantity(parseInt(e.target.value) || 0)}
            fullWidth
            inputProps={{ min: 0 }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateStockDialogOpen(false)}>{t('gwp.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleUpdateStockSubmit}
            disabled={updateStockMutation.isPending || newStockQuantity < 0}
          >
            {updateStockMutation.isPending ? <CircularProgress size={20} /> : t('gwp.update')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Campaign Detail Sub-Component (rendered inside expanded row)
// ---------------------------------------------------------------------------

interface CampaignDetailSectionProps {
  campaignId: string;
  autoCalculateNeeds: boolean;
  detail: GWPCampaignDetail | null;
  loading: boolean;
  onCalculateNeeds: () => void;
  calculateNeedsLoading: boolean;
  onDistribute: (campaignId: string, storeId: string, storeName: string) => void;
  onUpdateStock: (campaignId: string, storeId: string, storeName: string) => void;
  t: (key: string, options?: any) => any;
}

const CampaignDetailSection: React.FC<CampaignDetailSectionProps> = ({
  campaignId,
  autoCalculateNeeds,
  detail,
  loading,
  onCalculateNeeds,
  calculateNeedsLoading,
  onDistribute,
  onUpdateStock,
  t,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const stores = detail?.stores ?? [];

  return (
    <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('gwp.storeInventoryDetail')}
        </Typography>
        <Tooltip title={!autoCalculateNeeds ? t('gwp.autoCalculateDisabled') : ''}>
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={calculateNeedsLoading ? <CircularProgress size={16} /> : <CalculateIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onCalculateNeeds();
              }}
              disabled={!autoCalculateNeeds || calculateNeedsLoading}
            >
              {t('gwp.calculateNeeds')}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('gwp.store')}</TableCell>
              <TableCell align="right">{t('gwp.currentGiftStock')}</TableCell>
              <TableCell align="right">{t('gwp.allocated')}</TableCell>
              <TableCell align="right">{t('gwp.distributed')}</TableCell>
              <TableCell align="right">{t('gwp.estimatedNeed')}</TableCell>
              <TableCell align="right">{t('gwp.deficit')}</TableCell>
              <TableCell align="center">{t('gwp.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stores.length > 0 ? (
              stores.map((store) => (
                <TableRow key={store.store_id}>
                  <TableCell>{store.store_name || store.store_id}</TableCell>
                  <TableCell align="right">{store.current_gift_stock}</TableCell>
                  <TableCell align="right">{store.allocated}</TableCell>
                  <TableCell align="right">{store.distributed}</TableCell>
                  <TableCell align="right">{store.estimated_need}</TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{ color: store.deficit > 0 ? 'error.main' : 'text.primary', fontWeight: store.deficit > 0 ? 600 : 400 }}
                    >
                      {store.deficit}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('gwp.distributeGift')}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDistribute(campaignId, store.store_id, store.store_name || store.store_id);
                        }}
                      >
                        <DistributeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('gwp.updateStock')}>
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onUpdateStock(campaignId, store.store_id, store.store_name || store.store_id);
                        }}
                      >
                        <StockIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {t('gwp.noStoreData')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default GWPManagement;
